import { DOMNode, Element } from 'html-react-parser'
import { markup } from '@lifedot/utils'
import { PostCard } from '@/components/ShortCode/PostCard'
import { CityLinksProps } from '@/components/ShortCode/CityLinks/CityLinks'
import { Toc } from '@/components/Post/Toc'
import dynamic from 'next/dynamic'
import { PrefectureLinks } from '@/components/ShortCode/PrefectureLinks'
import { TabbedCemeteryCard } from '@/components/ShortCode/TabbedCemeteryCard'
import { CemeteryIntroduction } from '@/components/ShortCode/CemeteryIntroduction'
import {
  CityLinksPrefecturesKeys,
  cityLinksPrefectures
} from '@/components/ShortCode/CityLinks/cityLinksPrefectures'
import { CemeteriesTable } from '@/components/ShortCode/CemeteriesTable'
import { DirectorCemeteryCard } from '@/components/ShortCode/DirectorCemeteryCard'
import { PostContent } from '@lifedot/components/PostContent'
import { css } from '@emotion/react'
import { allSearchChoices } from '@lifedot/constants/searchChoices'
import { prefectures } from '@lifedot/constants/prefectures'
import { mq } from '@lifedot/styles/mediaQuery'
import { DialogueLink } from '@/components/ShortCode/DialogueLink'
import { isElement } from '@lifedot/utils'
import { Map } from '@/components/Map/Map'

const styles = {
  postCard: css({
    marginBottom: 12
  }),
  cemetery: css({
    marginBottom: 28
  }),
  links: css({
    display: 'flex',
    justifyContent: 'center',
    margin: '0 auto 1.6rem'
  }),
  directorCard: css({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
    [mq('sp')]: {
      marginBottom: 16
    }
  })
}

type ShortcodeParams = {
  key: string
  value: string
}[]

export const replaceShortCode = (domNode: DOMNode): JSX.Element | void => {
  if (!isElement(domNode)) return

  if (domNode.attribs.class === 'toc-element' && domNode.attribs['data-toc'])
    return <Toc toc={JSON.parse(domNode.attribs['data-toc'])} />

  if (domNode.attribs.class === 'shortcode-element') {
    if (domNode.attribs['data-shortcode-code'] === 'blogcard')
      return _replacePostCard(domNode)
    if (
      domNode.attribs['data-shortcode-code'] ===
      'cemetery_search_map_city_region'
    )
      return _replaceCityLinks(domNode)
    if (domNode.attribs['data-shortcode-code'] === 'comparison_list_box')
      return _replaceCemeteriesTable(domNode)
    if (
      ['cemetery_search_map', 'cemetery_terms'].includes(
        domNode.attribs['data-shortcode-code'] ?? ''
      )
    )
      return _replacePrefectureLink(domNode)

    if (
      ['simple_ids_box', 'ids_box'].includes(
        domNode.attribs['data-shortcode-code'] ?? ''
      )
    )
      return _replaceTabbedCemeteryCard(domNode)

    if (domNode.attribs['data-shortcode-code'] === 'snippet')
      return _replaceSnippet(domNode)

    if (domNode.attribs['data-shortcode-code'] === 'feature_box')
      return _replaceDirectorCemeteryCard(domNode)

    if (domNode.attribs['data-shortcode-code'] === 'dialogue_link')
      return _replaceDialogueLink(domNode)

    if (domNode.attribs['data-shortcode-code'] === 'introduction')
      return _replaceCemeteryIntroduction(domNode)

    if (domNode.attribs['data-shortcode-code'] === 'show_cemetery_google_map')
      return _replaceMap(domNode)

    return <div className="shortcode" />
  }

  if (domNode.name === 'script' && domNode.firstChild?.type === 'text') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const data = domNode.firstChild.data ?? ''
    return (
      <script {...domNode.attribs} dangerouslySetInnerHTML={markup(data)} />
    )
  }
}

const _replaceMap = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return
  const [data] = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams
  const ids = getCemeteryIds(data?.value ?? '')
  return <Map ids={ids} />
}

const _replacePostCard = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const [data] = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams
  return (
    <div css={styles.postCard}>
      <PostCard slug={data?.value ?? ''} />
    </div>
  )
}

const getCemeteryIds = (value: string): number[] =>
  value
    .split(/[^\d]/)
    .reduce<number[]>(
      (res, value) => (value ? [...res, Number(value)] : res),
      []
    )

const _replaceCemeteryIntroduction = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const parsedData = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams

  const [data] = parsedData
  const searchType = parsedData.find(({ key }) =>
    ['service_search_choice', 'search_type'].includes(key)
  )

  if (!searchType) return
  return (
    <>
      {getCemeteryIds(data?.value ?? '').map((id, index) => (
        <CemeteryIntroduction
          key={`${id}_${index}`}
          cemeteryId={id}
          choiceCode={searchType.value}
        />
      ))}
    </>
  )
}

const _replaceTabbedCemeteryCard = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const [data] = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams

  return (
    <>
      {getCemeteryIds(data?.value ?? '').map((id, index) => (
        <TabbedCemeteryCard key={`${id}_${index}`} cemeteryId={id} />
      ))}
    </>
  )
}

const _replaceCemeteriesTable = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const [data] = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams

  return (
    <div css={styles.cemetery}>
      <CemeteriesTable cemeteryIds={getCemeteryIds(data?.value ?? '')} />
    </div>
  )
}

const _replaceCityLinks = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const data = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams
  const prefectureRoma =
    data.find(({ key }) => key === 'prefecture')?.value ?? ''
  const choiceCode =
    data.find(({ key }) => key === 'service_search_choice')?.value ?? ''
  const condition = allSearchChoices.find(({ code }) => choiceCode === code)

  if (!isCityLinksPrefecture(prefectureRoma) || !condition) return

  const prefectureName = prefectures[prefectureRoma]

  const CityLinks = dynamic<CityLinksProps>(() =>
    import('@/components/ShortCode/CityLinks').then((mod) => mod.CityLinks)
  )

  return (
    <>
      <p>
        実際に{prefectureName}の希望エリアで{condition.name}
        を調べてみたい方は、こちらから検索できます。
      </p>
      <div css={styles.links}>
        <CityLinks {...{ prefectureRoma, condition }} />
      </div>
    </>
  )
}

const isCityLinksPrefecture = (key: string): key is CityLinksPrefecturesKeys =>
  Object.keys(cityLinksPrefectures).includes(key)

const _replacePrefectureLink = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return
  const data = (
    JSON.parse(domNode.attribs['data-shortcode-params']) as ShortcodeParams
  ).find(({ key }) => ['service_search_choice', 'search_type'].includes(key))

  return (
    <div css={styles.links}>
      <PrefectureLinks choiceCode={data?.value} />
    </div>
  )
}

const _replaceDirectorCemeteryCard = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const [data] = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams

  return (
    <>
      {getCemeteryIds(data?.value ?? '').map((id, index) => (
        <div css={styles.directorCard} key={`${id}_${index}`}>
          <DirectorCemeteryCard cemeteryId={id} />
        </div>
      ))}
    </>
  )
}

const _replaceDialogueLink = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  return (
    <div css={styles.links}>
      <DialogueLink />
    </div>
  )
}

const _replaceSnippet = (domNode: Element) => {
  if (!domNode.attribs['data-shortcode-params']) return

  const [data] = JSON.parse(
    domNode.attribs['data-shortcode-params']
  ) as ShortcodeParams

  if (!data?.value) return
  return <PostContent contentHtml={data.value} />
}
